import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { CheckCircle2, CornerDownRight, Flag, Pencil, Trash2 } from "lucide-react";
import {
  addForumComment,
  deleteForumComment,
  markBestAnswer,
  reportForumContent,
  updateForumComment,
} from "@/lib/forum.functions";
import { useAuth } from "@/hooks/use-auth";
import { VoteButtons, SignInHint } from "./VoteButtons";
import { ForumImages } from "./ForumImages";
import { AuthorBadge } from "./AuthorBadge";
import { ForumBody } from "@/lib/forum-linkify";

export type ForumComment = {
  id: string;
  post_id: string;
  parent_id: string | null;
  user_id: string;
  body: string;
  image_urls: string[];
  is_best_answer: boolean;
  vote_score: number;
  created_at: string;
  edited_at?: string | null;
  author: { display_name: string | null; avatar_url: string | null; badge?: string | null };
};

type NodeType = ForumComment & { children: NodeType[] };

function buildTree(comments: ForumComment[]): NodeType[] {
  const byId = new Map<string, NodeType>();
  for (const c of comments) byId.set(c.id, { ...c, children: [] });
  const roots: NodeType[] = [];
  for (const c of byId.values()) {
    if (c.parent_id && byId.has(c.parent_id)) byId.get(c.parent_id)!.children.push(c);
    else roots.push(c);
  }
  return roots;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return "nyss";
  if (min < 60) return `${min} min sedan`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} tim sedan`;
  const d = Math.round(h / 24);
  if (d < 31) return `${d} d sedan`;
  return new Date(iso).toLocaleDateString("sv-SE");
}

/** Renderar @omnämnanden och naturliga länkar (Fyndanu, våra sidor). */
function BodyText({ body }: { body: string }) {
  return (
    <ForumBody
      body={body}
      className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/90"
    />
  );
}


export function CommentThread({
  postId,
  postAuthorId,
  locked,
  comments,
  isQuestion,
  lastReadAt,
}: {
  postId: string;
  postAuthorId: string;
  locked: boolean;
  comments: ForumComment[];
  isQuestion: boolean;
  lastReadAt?: string | null;
}) {
  const { signedIn } = useAuth();
  const tree = useMemo(() => buildTree(comments), [comments]);
  const newCount = useMemo(() => {
    if (!lastReadAt) return 0;
    const t = new Date(lastReadAt).getTime();
    return comments.filter((c) => new Date(c.created_at).getTime() > t).length;
  }, [comments, lastReadAt]);

  return (
    <section className="mt-10 border-t border-border pt-8">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl md:text-3xl">
          {comments.length} {comments.length === 1 ? "svar" : "svar"}
        </h2>
        {newCount > 0 && (
          <span className="rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-accent">
            {newCount} nytt sedan ditt senaste besök
          </span>
        )}
      </div>

      {locked ? (
        <p className="mt-4 rounded-2xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          Tråden är låst — inga nya svar kan läggas till.
        </p>
      ) : signedIn ? (
        <div className="mt-5">
          <CommentForm postId={postId} parentId={null} placeholder="Skriv ditt svar… tips: skriv @namn för att nämna någon" />
        </div>
      ) : (
        <div className="mt-5">
          <SignInHint what="svara i tråden" />
        </div>
      )}

      <ul className="mt-8 space-y-5">
        {tree.map((node) => (
          <CommentNode
            key={node.id}
            node={node}
            depth={0}
            postId={postId}
            postAuthorId={postAuthorId}
            locked={locked}
            isQuestion={isQuestion}
            lastReadAt={lastReadAt}
          />
        ))}
        {!comments.length && (
          <li className="text-sm text-muted-foreground">Inga svar ännu — bli först att svara.</li>
        )}
      </ul>
    </section>
  );
}

function CommentNode({
  node,
  depth,
  postId,
  postAuthorId,
  locked,
  isQuestion,
  lastReadAt,
}: {
  node: NodeType;
  depth: number;
  postId: string;
  postAuthorId: string;
  locked: boolean;
  isQuestion: boolean;
  lastReadAt?: string | null;
}) {
  const { user, signedIn } = useAuth();
  const qc = useQueryClient();
  const del = useServerFn(deleteForumComment);
  const best = useServerFn(markBestAnswer);
  const report = useServerFn(reportForumContent);
  const save = useServerFn(updateForumComment);
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(node.body);
  const [saving, setSaving] = useState(false);

  const isOwn = user?.id === node.user_id;
  const isOp = user?.id === postAuthorId;
  const isNew =
    !!lastReadAt && new Date(node.created_at).getTime() > new Date(lastReadAt).getTime();

  async function remove() {
    if (!confirm("Ta bort svaret?")) return;
    try {
      await del({ data: { id: node.id } });
      qc.invalidateQueries({ queryKey: ["forum"] });
      toast.success("Svaret borttaget");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte ta bort");
    }
  }

  async function saveEdit() {
    if (draft.trim().length < 2) return;
    setSaving(true);
    try {
      await save({ data: { id: node.id, body: draft.trim() } });
      qc.invalidateQueries({ queryKey: ["forum"] });
      setEditing(false);
      toast.success("Svaret uppdaterat");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  }

  async function pickBest() {
    try {
      await best({ data: { post_id: postId, comment_id: node.id } });
      qc.invalidateQueries({ queryKey: ["forum"] });
      toast.success("Markerat som bästa svar");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte markera");
    }
  }

  async function flag() {
    const reason = prompt("Varför rapporterar du svaret? (spam, hot, off-topic…)");
    if (!reason) return;
    try {
      await report({ data: { comment_id: node.id, reason } });
      toast.success("Tack — vi tittar på det");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte rapportera");
    }
  }

  return (
    <li id={`comment-${node.id}`} className={depth > 0 ? "border-l border-border pl-3 sm:pl-5" : ""}>
      <div
        className={`scroll-mt-24 rounded-2xl border p-4 ${
          node.is_best_answer
            ? "border-emerald-500/40 bg-emerald-500/5"
            : isNew
            ? "border-accent/40 bg-accent/[0.04]"
            : "border-border bg-card"
        }`}
      >
        {node.is_best_answer && (
          <p className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-600">
            <CheckCircle2 size={12} /> Bästa svar
          </p>
        )}
        <div className="flex items-start gap-3">
          <VoteButtons commentId={node.id} score={node.vote_score} />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground">
              <Link
                to="/forum/medlem/$id"
                params={{ id: node.user_id }}
                className="font-semibold text-foreground transition hover:text-accent"
              >
                {node.author.display_name || "Medlem"}
              </Link>
              <AuthorBadge badge={node.author.badge} className="ml-1.5" />

              {node.user_id === postAuthorId && (
                <span className="ml-1.5 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-bold uppercase">OP</span>
              )}{" "}
              · {timeAgo(node.created_at)}
              {node.edited_at ? <span className="ml-1 italic">· redigerad</span> : null}
              {isNew && (
                <span className="ml-1.5 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-bold uppercase text-accent">
                  Nytt
                </span>
              )}
            </p>

            {editing ? (
              <div className="mt-2 space-y-2">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={4}
                  maxLength={10000}
                  className="w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={saveEdit}
                    disabled={saving}
                    className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-60"
                  >
                    {saving ? "Sparar…" : "Spara"}
                  </button>
                  <button
                    onClick={() => {
                      setEditing(false);
                      setDraft(node.body);
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            ) : (
              <BodyText body={node.body} />
            )}
            <ForumImages paths={node.image_urls ?? []} className="mt-3" />

            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {!locked && signedIn && (
                <button onClick={() => setReplying((v) => !v)} className="inline-flex items-center gap-1 hover:text-foreground">
                  <CornerDownRight size={13} /> Svara
                </button>
              )}
              {isQuestion && isOp && !node.is_best_answer && (
                <button onClick={pickBest} className="inline-flex items-center gap-1 hover:text-emerald-600">
                  <CheckCircle2 size={13} /> Bästa svar
                </button>
              )}
              {isOwn && !editing && (
                <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 hover:text-foreground">
                  <Pencil size={13} /> Redigera
                </button>
              )}
              {isOwn && (
                <button onClick={remove} className="inline-flex items-center gap-1 hover:text-destructive">
                  <Trash2 size={13} /> Ta bort
                </button>
              )}
              {signedIn && !isOwn && (
                <button onClick={flag} className="inline-flex items-center gap-1 hover:text-foreground">
                  <Flag size={13} /> Rapportera
                </button>
              )}
            </div>

            {replying && (
              <div className="mt-3">
                <CommentForm
                  postId={postId}
                  parentId={node.id}
                  placeholder="Ditt svar…"
                  initialBody={
                    node.author.display_name ? `@${node.author.display_name} ` : ""
                  }
                  onDone={() => setReplying(false)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {node.children.length > 0 && (
        <ul className="mt-4 space-y-4">
          {node.children.map((child) => (
            <CommentNode
              key={child.id}
              node={child}
              depth={depth + 1}
              postId={postId}
              postAuthorId={postAuthorId}
              locked={locked}
              isQuestion={isQuestion}
              lastReadAt={lastReadAt}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function CommentForm({
  postId,
  parentId,
  placeholder,
  onDone,
  initialBody = "",
}: {
  postId: string;
  parentId: string | null;
  placeholder: string;
  onDone?: () => void;
  initialBody?: string;
}) {
  const add = useServerFn(addForumComment);
  const qc = useQueryClient();
  const [body, setBody] = useState(initialBody);
  const [posting, setPosting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (body.trim().length < 2) return;
    setPosting(true);
    try {
      await add({ data: { post_id: postId, parent_id: parentId, body: body.trim() } });
      setBody("");
      qc.invalidateQueries({ queryKey: ["forum"] });
      toast.success("Svar publicerat");
      onDone?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte publicera");
    } finally {
      setPosting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-2">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        maxLength={10000}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
      />
      <div className="flex items-center gap-2">
        <button
          disabled={posting || body.trim().length < 2}
          className="rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {posting ? "Publicerar…" : "Publicera svar"}
        </button>
        {onDone && (
          <button type="button" onClick={onDone} className="text-sm text-muted-foreground hover:text-foreground">
            Avbryt
          </button>
        )}
      </div>
    </form>
  );
}
